import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Grid,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  LinearProgress,
  Box,
  IconButton,
  Breadcrumbs,
  Link,
  Typography,
  Chip,
  InputAdornment,
  Menu,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import SecurityIcon from '@mui/icons-material/Security';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { UserContext } from '../../components/shared/useUser';
import { Section } from '../../components/ui/Section';
import {
  DocumentFoldersClient,
  DocumentsClient,
  DocumentFolderDto,
  DocumentDto,
  formatFileSize,
} from '../../app/services/api/documentsClient';
import { DocumentScope, DocumentCategory, DocumentType } from '../../enums/DocumentPermission';

const foldersClient = new DocumentFoldersClient();
const documentsClient = new DocumentsClient();

const Documents: React.FC = () => {
  const { t } = useTranslation('documents');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermissionTo, selectedFranchise } = useContext(UserContext);

  // const canView = hasPermissionTo ? hasPermissionTo(['DocumentsView']) : true;
  const canUpload = hasPermissionTo ? hasPermissionTo(['DocumentsUpload']) : true;
  const canCreateFolder = hasPermissionTo ? hasPermissionTo(['DocumentsCreate']) : true;
  const canDelete = hasPermissionTo ? hasPermissionTo(['DocumentsDelete']) : true;
  const canManagePermissions = hasPermissionTo ? hasPermissionTo(['DocumentsManagePermissions']) : true;

  const [loading, setLoading] = useState(true);
  const [currentFolder, setCurrentFolder] = useState<DocumentFolderDto | null>(null);
  const [ancestorFolders, setAncestorFolders] = useState<DocumentFolderDto[]>([]);
  const [folders, setFolders] = useState<DocumentFolderDto[]>([]);
  const [documents, setDocuments] = useState<DocumentDto[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchFolders, setSearchFolders] = useState<DocumentFolderDto[]>([]);
  const [searchDocuments, setSearchDocuments] = useState<DocumentDto[]>([]);
  const [scopeFilter, setScopeFilter] = useState<DocumentScope | ''>('');
  const [categoryFilter, setCategoryFilter] = useState<DocumentCategory | ''>('');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Context menu
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    item: DocumentFolderDto | DocumentDto;
    type: 'folder' | 'document';
  } | null>(null);

  // Create folder dialog
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderDescription, setNewFolderDescription] = useState('');
  const [newFolderScope, setNewFolderScope] = useState<DocumentScope>(DocumentScope.Franchise);
  const [newFolderCategory, setNewFolderCategory] = useState<DocumentCategory>(DocumentCategory.General);

  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ item: DocumentFolderDto | DocumentDto; type: 'folder' | 'document' } | null>(null);

  const currentFolderId = searchParams.get('folderId');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (currentFolderId) {
        // Load specific folder, its contents, and ancestors for breadcrumb navigation
        const [folder, ancestors] = await Promise.all([
          foldersClient.getFolder(Number(currentFolderId)),
          foldersClient.getAncestors(Number(currentFolderId)),
        ]);
        setCurrentFolder(folder);
        setAncestorFolders(ancestors);
        const [childFolders, docs] = await Promise.all([
          foldersClient.getChildFolders(Number(currentFolderId)),
          documentsClient.getDocuments(Number(currentFolderId)),
        ]);
        setFolders(childFolders);
        setDocuments(docs);
      } else {
        // Load root folders
        setCurrentFolder(null);
        setAncestorFolders([]);
        const rootFolders = await foldersClient.getRootFolders(
          scopeFilter || undefined,
          selectedFranchise?.franchiseId,
          undefined, // Store filtering would need storeNumber conversion
        );
        setFolders(rootFolders);
        setDocuments([]);
      }
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  }, [currentFolderId, scopeFilter, selectedFranchise?.franchiseId]);

  // Search across all folders and documents
  const performSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setIsSearching(false);
      setSearchFolders([]);
      setSearchDocuments([]);
      return;
    }

    setLoading(true);
    setIsSearching(true);
    try {
      const [folderResults, documentResults] = await Promise.all([
        foldersClient.searchFolders(query, selectedFranchise?.franchiseId),
        documentsClient.searchDocuments({ query, franchiseId: selectedFranchise?.franchiseId }),
      ]);
      setSearchFolders(folderResults);
      setSearchDocuments(documentResults.documents);
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedFranchise?.franchiseId]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      performSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, performSearch]);

  useEffect(() => {
    if (!isSearching) {
      loadData();
    }
  }, [loadData, isSearching]);

  const handleFolderClick = (folder: DocumentFolderDto) => {
    setSearchQuery(''); // Clear search when navigating
    setSearchParams({ folderId: folder.id.toString() });
  };

  const handleBreadcrumbClick = (folderId: number | null) => {
    setSearchQuery(''); // Clear search when navigating
    if (folderId === null) {
      setSearchParams({});
    } else {
      setSearchParams({ folderId: folderId.toString() });
    }
  };

  const handleDocumentClick = (document: DocumentDto) => {
    navigate(`/documents/view/${document.id}`);
  };

  const handleContextMenu = (
    event: React.MouseEvent,
    item: DocumentFolderDto | DocumentDto,
    type: 'folder' | 'document',
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({
      mouseX: event.clientX,
      mouseY: event.clientY,
      item,
      type,
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      await foldersClient.createFolder({
        name: newFolderName,
        description: newFolderDescription || undefined,
        parentFolderId: currentFolder?.id ?? undefined,
        scope: newFolderScope,
        category: newFolderCategory,
        franchiseId: selectedFranchise?.franchiseId,
        storeId: undefined, // Would need storeNumber to storeId conversion
      });
      setCreateFolderOpen(false);
      setNewFolderName('');
      setNewFolderDescription('');
      loadData();
    } catch (error) {
      console.error('Error creating folder:', error);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      if (itemToDelete.type === 'folder') {
        await foldersClient.deleteFolder((itemToDelete.item as DocumentFolderDto).id);
      } else {
        await documentsClient.deleteDocument((itemToDelete.item as DocumentDto).id);
      }
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      loadData();
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const getDocumentIcon = (documentType: DocumentType) => {
    switch (documentType) {
      case DocumentType.PDF:
        return <PictureAsPdfIcon color="error" />;
      case DocumentType.Image:
        return <ImageIcon color="primary" />;
      case DocumentType.Video:
        return <VideoFileIcon color="secondary" />;
      case DocumentType.Document:
        return <DescriptionIcon color="info" />;
      case DocumentType.Spreadsheet:
        return <TableChartIcon color="success" />;
      case DocumentType.Presentation:
        return <SlideshowIcon sx={{ color: 'orange' }} />;
      default:
        return <InsertDriveFileIcon />;
    }
  };

  const getScopeChipColor = (scope: DocumentScope): 'default' | 'primary' | 'secondary' | 'success' | 'warning' => {
    switch (scope) {
      case DocumentScope.System:
        return 'secondary';
      case DocumentScope.Franchise:
        return 'primary';
      case DocumentScope.Store:
        return 'success';
      default:
        return 'default';
    }
  };

  // Use search results when searching, otherwise use current folder contents
  const displayFolders = isSearching ? searchFolders : folders;
  const displayDocuments = isSearching ? searchDocuments : documents;

  const filteredFolders = displayFolders.filter(f => {
    if (categoryFilter && f.category !== categoryFilter) {
      return false;
    }
    return true;
  });

  const filteredDocuments = displayDocuments;

  const allItems = [
    ...filteredFolders.map(f => ({ ...f, _type: 'folder' as const })),
    ...filteredDocuments.map(d => ({ ...d, _type: 'document' as const })),
  ];

  const paginatedItems = allItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Build breadcrumb path
  const breadcrumbPath = currentFolder?.path ?? [];

  return (
    <PageArea>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <PageTitle subTitle={t('pageSubtitle', 'Manage franchise documentation, training guides, policies, and task evidence')}>
          {t('pageTitle', 'Document Management')}
        </PageTitle>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canCreateFolder && (
            <Button
              variant="secondary"
              startIcon={<CreateNewFolderIcon />}
              onClick={() => setCreateFolderOpen(true)}
            >
              {t('newFolderButton', 'New Folder')}
            </Button>
          )}
          {canUpload && currentFolder && (
            <Button
              variant="primary"
              startIcon={<UploadFileIcon />}
              onClick={() => navigate(`/documents/upload?folderId=${currentFolder.id}`)}
            >
              {t('uploadButton', 'Upload')}
            </Button>
          )}
        </Box>
      </Box>

      {/* Filters - Above Section */}
      <Box sx={{ mt: 3, mb: 2 }}>
        <Grid container
          spacing={2}
          alignItems="center">
          <Grid item
            xs={12}
            md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('searchPlaceholder', 'Search documents...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item
            xs={6}
            md={2}>
            <TextField
              fullWidth
              size="small"
              select
              label={t('scopeLabel', 'Scope')}
              value={scopeFilter}
              onChange={(e) => setScopeFilter(e.target.value as DocumentScope | '')}
            >
              <MenuItem value="">{t('allScopes', 'All Scopes')}</MenuItem>
              <MenuItem value={DocumentScope.Franchise}>{t('scopeFranchise', 'Franchise')}</MenuItem>
              <MenuItem value={DocumentScope.Store}>{t('scopeStore', 'Store')}</MenuItem>
            </TextField>
          </Grid>
          <Grid item
            xs={6}
            md={2}>
            <TextField
              fullWidth
              size="small"
              select
              label={t('categoryLabel', 'Category')}
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value as DocumentCategory | '')}
            >
              <MenuItem value="">{t('allCategories', 'All Categories')}</MenuItem>
              {Object.values(DocumentCategory).map(cat => (
                <MenuItem key={cat}
                  value={cat}>{cat}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Box>

      <Section>
        {/* Breadcrumbs - Section Header */}
        <Box sx={{ p: '12px', borderBottom: '1px solid', borderColor: 'divider' }}>
          {isSearching ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SearchIcon fontSize="small" />
              <Typography color="text.primary">
                {t('searchResultsFor', 'Search Results for "{{query}}"', { query: searchQuery })}
              </Typography>
              <Chip
                size="small"
                label={t('resultCount', '{{count}} results', { count: allItems.length })}
                variant="outlined"
              />
            </Box>
          ) : (
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
              <Link
                component="button"
                underline="hover"
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                color="inherit"
                onClick={() => handleBreadcrumbClick(null)}
              >
                {t('documentsBreadcrumb', 'Documents')}
              </Link>
              {breadcrumbPath.map((segment, index) => {
                const isLast = index === breadcrumbPath.length - 1;
                // Use ancestorFolders for navigation - they're ordered from root to parent
                const ancestorFolder = ancestorFolders[index];

                return isLast ? (
                  <Typography key={segment}
                    color="text.primary"
                    sx={{ display: 'flex', alignItems: 'center' }}>
                    <FolderOpenIcon sx={{ mr: 0.5 }}
                      fontSize="inherit" />
                    {segment}
                  </Typography>
                ) : (
                  <Link
                    key={segment}
                    component="button"
                    underline="hover"
                    sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                    color="inherit"
                    onClick={() => ancestorFolder && handleBreadcrumbClick(ancestorFolder.id)}
                  >
                    <FolderIcon sx={{ mr: 0.5 }}
                      fontSize="inherit" />
                    {segment}
                  </Link>
                );
              })}
            </Breadcrumbs>
          )}
        </Box>

        {/* Loading indicator */}
        {loading && <LinearProgress />}

        {/* Content table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="50%">{t('columnName', 'Name')}</TableCell>
                <TableCell>{t('columnType', 'Type')}</TableCell>
                <TableCell>{t('columnSize', 'Size')}</TableCell>
                <TableCell>{t('columnModified', 'Modified')}</TableCell>
                <TableCell width="50px"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && paginatedItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}
                    align="center"
                    sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {currentFolder ? t('folderEmptyText', 'This folder is empty') : t('noDocumentsFoundText', 'No documents found')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {paginatedItems.map((item) => {
                if (item._type === 'folder') {
                  const folder = item as DocumentFolderDto & { _type: 'folder' };
                  return (
                    <TableRow
                      key={`folder-${folder.id}`}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleFolderClick(folder)}
                      onContextMenu={(e) => handleContextMenu(e, folder, 'folder')}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FolderIcon color="warning" />
                          <Typography fontWeight={500}>{folder.name}</Typography>
                          <Chip
                            size="small"
                            label={folder.scope}
                            color={getScopeChipColor(folder.scope)}
                            sx={{ ml: 1 }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip size="small"
                          label={folder.category}
                          variant="outlined" />
                      </TableCell>
                      <TableCell>
                        {t('folderContentsCount', '{{folders}} folders, {{files}} files', { folders: folder.childFolderCount, files: folder.documentCount })}
                      </TableCell>
                      <TableCell>
                        {folder.updatedAt
                          ? new Date(folder.updatedAt).toLocaleDateString()
                          : new Date(folder.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleContextMenu(e, folder, 'folder');
                          }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                } else {
                  const doc = item as DocumentDto & { _type: 'document' };
                  return (
                    <TableRow
                      key={`doc-${doc.id}`}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleDocumentClick(doc)}
                      onContextMenu={(e) => handleContextMenu(e, doc, 'document')}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getDocumentIcon(doc.latestVersion.documentType)}
                          <Box>
                            <Typography fontWeight={500}>{doc.name}</Typography>
                            {doc.description && (
                              <Typography
                                variant="textXS"
                                color="text.secondary"
                              >
                                {doc.description}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip size="small"
                          label={doc.latestVersion.documentType}
                          variant="outlined" />
                      </TableCell>
                      <TableCell>{formatFileSize(doc.latestVersion.fileSize)}</TableCell>
                      <TableCell>
                        {doc.updatedAt
                          ? new Date(doc.updatedAt).toLocaleDateString()
                          : new Date(doc.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleContextMenu(e, doc, 'document');
                          }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                }
              })}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  count={allItems.length}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50]}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Section>

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        {contextMenu?.type === 'document' && (
          <MenuItem onClick={() => {
            // Handle download
            handleCloseContextMenu();
          }}>
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('downloadMenuItem', 'Download')}</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => {
          if (!contextMenu) return;
          if (contextMenu.type === 'folder') {
            navigate(`/documents/folder/${(contextMenu.item as DocumentFolderDto).id}/edit`);
          } else {
            navigate(`/documents/edit/${(contextMenu.item as DocumentDto).id}`);
          }
          handleCloseContextMenu();
        }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('editMenuItem', 'Edit')}</ListItemText>
        </MenuItem>
        {contextMenu?.type === 'folder' && canManagePermissions && (
          <MenuItem onClick={() => {
            navigate(`/documents/folder/${(contextMenu.item as DocumentFolderDto).id}/permissions`);
            handleCloseContextMenu();
          }}>
            <ListItemIcon>
              <SecurityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('managePermissionsMenuItem', 'Manage Permissions')}</ListItemText>
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem onClick={() => {
            setItemToDelete({
              item: contextMenu!.item,
              type: contextMenu!.type,
            });
            setDeleteDialogOpen(true);
            handleCloseContextMenu();
          }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small"
                color="error" />
            </ListItemIcon>
            <ListItemText>{t('deleteMenuItem', 'Delete')}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Create Folder Dialog */}
      <Dialog open={createFolderOpen}
        onClose={() => setCreateFolderOpen(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>{t('createFolderDialogTitle', 'Create New Folder')}</DialogTitle>
        <DialogContent>
          <Grid container
            spacing={2}
            sx={{ mt: 1 }}>
            <Grid item
              xs={12}>
              <TextField
                fullWidth
                label={t('folderNameLabel', 'Folder Name')}
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                required
              />
            </Grid>
            <Grid item
              xs={12}>
              <TextField
                fullWidth
                label={t('descriptionLabel', 'Description')}
                value={newFolderDescription}
                onChange={(e) => setNewFolderDescription(e.target.value)}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item
              xs={6}>
              <TextField
                fullWidth
                select
                label={t('scopeLabel', 'Scope')}
                value={newFolderScope}
                onChange={(e) => setNewFolderScope(e.target.value as DocumentScope)}
              >
                <MenuItem value={DocumentScope.Franchise}>{t('scopeFranchise', 'Franchise')}</MenuItem>
                <MenuItem value={DocumentScope.Store}>{t('scopeStore', 'Store')}</MenuItem>
              </TextField>
            </Grid>
            <Grid item
              xs={6}>
              <TextField
                fullWidth
                select
                label={t('categoryLabel', 'Category')}
                value={newFolderCategory}
                onChange={(e) => setNewFolderCategory(e.target.value as DocumentCategory)}
              >
                {Object.values(DocumentCategory).filter(c => c !== DocumentCategory.Evidence).map(cat => (
                  <MenuItem key={cat}
                    value={cat}>{cat}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateFolderOpen(false)}>{t('cancel', 'Cancel')}</Button>
          <Button
            variant="primary"
            onClick={handleCreateFolder}
            disabled={!newFolderName.trim()}
          >
            {t('create', 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>
          {itemToDelete?.type === 'folder' ? t('deleteFolderTitle', 'Delete Folder?') : t('deleteDocumentTitle', 'Delete Document?')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t('deleteConfirmPrefix', 'Are you sure you want to delete')}{' '}
            <strong>
              {itemToDelete?.type === 'folder'
                ? (itemToDelete.item as DocumentFolderDto).name
                : (itemToDelete?.item as DocumentDto)?.name}
            </strong>
            {t('questionMark', '?')}
          </Typography>
          {itemToDelete?.type === 'folder' && (
            <Typography color="error"
              sx={{ mt: 1 }}>
              {t('deleteFolderWarning', 'Note: Folders must be empty before they can be deleted.')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('cancel', 'Cancel')}</Button>
          <Button
            variant="primary"
            color="error"
            onClick={handleDeleteConfirm}
          >
            {t('deleteConfirmButton', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </PageArea>
  );
};

export default Documents;
